import { useLazyQuery, useQuery } from "@apollo/client"
import moment from "moment"
import { FC, useEffect, useRef, useState } from "react"
import { Box, Flex, Text } from "rebass/styled-components"
import { Area, ComposedChart, ResponsiveContainer, Scatter, Tooltip, TooltipProps, XAxis, YAxis } from "recharts"
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent"
import styled from "styled-components"
import { Skeleton } from "../../components/loadingSkeleton/Skeleton"
import RadioButton from "../../components/RadioButton"
import { GET_LAB_CHART_VALUES } from "../../graphql/queries"
import { IGetLabChartValuesInput, IGetLabChartValuesOutput, ILabChartValue } from "../../graphql/types/queries/symptoms"
import { useIsVisible } from "../../hooks/useIsVisible"
import { CustomLegend } from "./components/LabsLegend"

const ToolTip = styled(Box)`
  padding: 0.8rem;
  border-radius: 10px;
  box-shadow: 3px 3px 3px rgba(0, 0, 0, 0.3);
`

const ContainerMobile = styled(Flex)`
  width: 100%;
  max-width: 100%;
    padding: 0 20px;
    overflow-x: hidden;
`;

const PeriodSelectorItem = styled(Flex) <{ isSelected: boolean }>`
  padding: 0 8px;
  border-radius: 24px;
  margin-left: 4px;
  margin-right: 4px;
  justify-content: center;
  align-items: center;
  background: ${(props) => (props.isSelected ? '#354E79' : 'transparent')};
  color: ${(props) => (!props.isSelected ? '#354E79' : 'white')};
`;

const PeriodSelector: FC<{
  period: string;
  onClick: () => void;
  isSelected: boolean;
  isDisabled: boolean;
}> = ({ period, onClick, isSelected, isDisabled }) => {
  if (isDisabled) return null;
  return (
    <PeriodSelectorItem isSelected={isSelected} onClick={onClick}>
      {period}
    </PeriodSelectorItem>
  );
};

const CustomizedTick = ({ x, y, payload }: any) => {
  return <text style={{ fontSize: "10px", fill: '#A3A3A3' }} x={x} y={y} textAnchor="top" dominantBaseline="hanging">
    {moment(payload.value).format('MMM DD YYYY')}
  </text>
}
const CustomizedTick2 = ({ x, y, payload }: any) => {
  // console.log(props)
  return <text style={{ fontSize: "10px", fill: '#A3A3A3' }} x={x - 24} y={y - 6} textAnchor="top" dominantBaseline="hanging">
    {payload.value}
  </text>
}
interface ILabTestsPoint { x: string; y: number | null; isPaloma?: boolean, unit: string; isSelfEntered?: boolean, fill?: string }

const getValues = (output: IGetLabChartValuesOutput, selectedMarker: 'TSH' |
  'ft4' |
  'ft3' |
  'tpo' |
  'rt3' |
  'vitD') => {
  const data: Array<{ x: string; y: null | number }> = []
  const labTestsPoints: ILabTestsPoint[] = []

  let lastAvg2: null | number = null
  let max = 1
  // useEffect(()=>{

  let vals: Array<ILabChartValue | null> = []
  let unit: string
  switch (selectedMarker) {
    case 'tpo':
      vals = output.providerGetLabChartValues.tpo || []
      unit = 'IU/mL'
      break;
    case 'ft3':
      vals = output.providerGetLabChartValues.ft3 || []
      unit = 'pg/mL'
      break;
    case 'ft4':
      vals = output.providerGetLabChartValues.ft4 || []
      unit = 'ng/dL'
      break;
    case 'rt3':
      vals = output.providerGetLabChartValues.rT3 || []
      unit = 'IU/mL'
      break;
    case 'vitD':
      vals = output.providerGetLabChartValues.vitD || []
      unit = 'ng/L'
      break;
    case 'TSH':
    default:
      vals = output.providerGetLabChartValues.tsh || []
      unit = 'uU/mL'
      break;
  }

  const sorted = (JSON.parse(JSON.stringify(vals)) as Array<ILabChartValue | null>).sort((a, b) => {
    if (!a || !a.date) return -1;
    if (!b || !b.date) return 1;
    let dta = a.date.indexOf('/') > -1 ? moment(a.date, 'MM/DD/YYYY').format('x') : a.date
    let dtB = b.date.indexOf('/') > -1 ? moment(b.date, 'MM/DD/YYYY').format('x') : b.date

    let res = 0

    try {
      res = Number(dta) < Number(dtB) ? -1 : 0
    } catch (error) {

    }

    return res
  })

  for (let index = 0; index < sorted.length; index++) {
    const element = sorted[index];
    if (!element) continue
    if (element.value) {
      lastAvg2 = (element.value !== null && element.value !== undefined ? element.value : 0) / 100
    }

    data.push({
      x: element.date,
      y: element.value !== null && element.value !== undefined ? element.value : null
    })

    max = Math.max(lastAvg2 || 1, max)

    if (element.value) {
      labTestsPoints.push({
        x: element.date.indexOf('-') > -1 || element.date.indexOf('/') > -1 ? moment(element.date).format('x') : element.date,
        y: (element.value !== null && element.value !== undefined ? element.value : 0) / 100,
        isPaloma: element.isPaloma,
        unit: element.units || unit,
        isSelfEntered: element.isSelfEntered,
        fill: element.isSelfEntered ? '#f1c40f' : 'white'
      })
    }
  }

  return { labTestsPoints, max, unit, prescriptions: output.providerGetLabChartValues.prescriptions || [] }
}
type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  fullArray?: Array<{
    x: string;
    y: number | null;
    isPaloma?: boolean;
  }> | null;
};

const CustomizedTooltip: FC<CustomTooltipProps & {
  selected?: string
}> = ({ active, payload, selected }) => {
  if (active && !!payload && payload.length > 0 && payload[0].payload) {
    const isDocOnly = !!payload[0].payload.__isDocOnly
    const hasDocs = payload[0].payload.documentDescriptions && payload[0].payload.documentDescriptions.length > 0
    console.log({ payload, isDocOnly, hasDocs })
    if (isDocOnly && hasDocs) {
      return (
        <ToolTip style={{ background: !!payload && payload.length > 0 && payload[0].payload.isSelfEntered ? '#f1c40f' : '#4E658A' }}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} fontSize='14px' color='white'>{!!payload && payload.length > 0 ? payload[0].payload.x : ''}</Text>
            <Flex flexDirection={'column'}>
              <Text fontWeight={500} fontSize='14px' color='white'>Prescriptions:</Text>
              {payload[0].payload.documentDescriptions.map((p: string) => (
                <Text key={p} fontWeight={300} fontSize='13px' color='white' ml={1}>- {p}</Text>
              ))}
            </Flex>
          </Flex>

        </ToolTip>
      )
    }
    return (
      <ToolTip style={{ background: !!payload && payload.length > 0 && payload[0].payload.isSelfEntered ? '#f1c40f' : '#4E658A' }}>
        <Flex flexDirection={'column'}>
          {!!payload && payload.length > 0 && payload[0].payload.isSelfEntered && (
            <Text fontWeight={500} fontSize='14px' color='white'>Self Entered</Text>
          )}
          <Text fontWeight={300} fontSize='14px' color='white'>{!!payload && payload.length > 0 ? payload[0].payload.x : ''}</Text>
          <Flex alignItems={'baseline'}>

            <Text fontWeight={500} fontSize='14px' color='white'>{selected}: {!!payload && payload.length > 0 && (!!payload[0].payload.y || payload[0].payload.y === 0) ? payload[0].payload.y : ''}</Text>
            <Text fontWeight={300} fontSize='10px' color='white'>{!!payload && payload.length > 0 && (!!payload[0].payload.units) ? payload[0].payload.units : ''}</Text>
          </Flex>
          {!!payload && payload.length > 0 && payload[0].payload.documentDescriptions && payload[0].payload.documentDescriptions.length > 0 && (
            <Flex flexDirection={'column'}>

              <Text fontWeight={500} fontSize='14px' color='white' mt={2}>Prescriptions:</Text>
              {payload[0].payload.documentDescriptions.map((p: string) => (
                <Text key={p} fontWeight={300} fontSize='13px' color='white' ml={1}>- {p}</Text>
              ))}
            </Flex>
          )}

        </Flex>

      </ToolTip>
    );
  }

  return null;
};

type CombinedEntry = {
  x: number | string; // epoch time as number
  y?: number | null;
  documentDescriptions?: string[];
  isPaloma?: boolean;
  isSelfEntered?: boolean
  units?: string
  isDocsOnly?: boolean
};
const CustomScatterShape = (props: any) => {
  const { cx, cy, payload, fill, stroke, strokeWidth } = props;
  let size = 6;
  const hasY = payload.y != null && !payload.__isDocOnly;
  const hasDocs = Array.isArray(payload.documentDescriptions) && payload.documentDescriptions.length > 0;

  if (hasY && hasDocs) {
    // Diamond
    return (
      <path
        d={`M${cx},${cy - size} L${cx + size},${cy} L${cx},${cy + size} L${cx - size},${cy} Z`}
        fill={'orange'} stroke={stroke} strokeWidth={strokeWidth}
      />
    );
  } else if (hasDocs) {
    // Triangle
    size = 8
    return (
      <path
        d={`M${cx},${cy - size} L${cx - size},${cy + size} L${cx + size},${cy + size} Z`}
        fill="#2ecaa5"
        stroke={"#2ecaa5"} strokeWidth={2}
      />
    );
  } else if (hasY) {
    return <circle cx={cx} cy={cy} r={size} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }

  return <circle cx={0} cy={0} r={0} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
};
const Labs = ({ id, isMinimized }: { id: string, isMinimized?: boolean }) => {
  const [selected, setSelected] = useState<
    'TSH' |
    'ft4' |
    'ft3' |
    'tpo' |
    'rt3' |
    'vitD'>('TSH')
  const [values, setValues] = useState<Array<{
    x: string;
    y: number | null;
    isPaloma?: boolean | undefined;
    units?: string
  }> | null>(null)
  const [showSelfEntered, setShowSelfEntered] = useState(true)
  const [combinedData, setCombinedData] = useState<Array<CombinedEntry>>([])
  const [max, setMax] = useState<number>(1)
  const [, setUnit] = useState<string>('')
  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);
  const [load, { data, loading, error }] = useLazyQuery<
    IGetLabChartValuesOutput, IGetLabChartValuesInput>(GET_LAB_CHART_VALUES, {
      variables: {
        patientId: id
      }
    })

  useEffect(() => {
    if (!!isVisible) {
      load()
    }
  }, [isVisible])

  useEffect(() => {
    if (loading || !data) {
      setValues(null)
      return
    }
    const vls = getValues(data, selected)
    const pres = vls.prescriptions.map((item) => ({
      x: item.x,
      documentDescriptions: item.documentDescriptions,
    }));
    // TODO: REMOVE next line!!!
    // pres[3].x = "1692687600000"
    const map = new Map<number, CombinedEntry>();

    vls.labTestsPoints.forEach((val) => {
      map.set(Number(val.x), { x: Number(val.x), y: val.y, isPaloma: val.isPaloma, units: val.unit, isSelfEntered: val.isSelfEntered });
    });

    pres.forEach((pre) => {
      if (map.has(Number(pre.x))) {
        const existing = map.get(Number(pre.x))!;
        existing.documentDescriptions = pre.documentDescriptions;
        map.set(Number(pre.x), existing);
      } else {
        map.set(Number(pre.x), { x: Number(pre.x), documentDescriptions: pre.documentDescriptions });
      }
    });
    let combined = Array.from(map.values()).sort((a, b) => Number(a.x) - Number(b.x));
    const combinedDataCleaned = combined.map(entry => ({
      ...entry,
      x: moment(entry.x, 'x').format('MM/DD/YYYY'), // or 'MM/dd/yyyy'
    }));
    setValues(vls.labTestsPoints)
    setCombinedData(combinedDataCleaned)

    setUnit(vls.unit)
    setMax(vls.max)
  }, [data, loading, selected])

  const allTicks = Array.from(new Set((combinedData || []).filter(x => showSelfEntered ? true : !x.isSelfEntered).map(d => d.x)));
  const displayData = (combinedData || []).filter(x => showSelfEntered ? true : !x.isSelfEntered).map((entry) => {
    if (entry.y != null) return entry;

    if (entry.documentDescriptions?.length) {
      return { ...entry, y: entry.y || 0, __isDocOnly: true };
    }

    return entry;
  });

  return (
    <Flex ref={ref} flexDirection={'column'} width='100%' padding={'20px'}>
      {!loading &&
        (!data?.providerGetLabChartValues.tsh || data?.providerGetLabChartValues.tsh.length < 1) &&
        (!data?.providerGetLabChartValues.ft4 || data?.providerGetLabChartValues.ft4.length < 1) &&
        (!data?.providerGetLabChartValues.ft3 || data?.providerGetLabChartValues.ft3.length < 1) &&
        (!data?.providerGetLabChartValues.tpo || data?.providerGetLabChartValues.tpo.length < 1) &&
        (!data?.providerGetLabChartValues.rT3 || data?.providerGetLabChartValues.rT3.length < 1) &&
        (!data?.providerGetLabChartValues.vitD || data?.providerGetLabChartValues.vitD.length < 1 &&
          (!data.providerGetLabChartValues.prescriptions || data.providerGetLabChartValues.prescriptions.length < 1)
        ) ?
        (<>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', }} paddingY={'12px'} mb={4}>
            <Flex>
              <Flex flexDirection={'column'} flex={1}>
                <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Labs Charts</Text>
                <Box flex={1}>
                  <Text textAlign={'center'} style={{ opacity: 0.3 }} mb={4}>No lab results</Text>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </>
        ) : (
          <>
            <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', }} paddingY={'12px'} paddingBottom={isMinimized ? '0px' : '12px'} mb={isMinimized ? 0 : 4}>
              <Flex flexDirection={isMinimized ? 'column' : 'row'}>

                <Flex flexDirection={'column'} flex={1}>
                  <Flex alignItems={'center'}>
                    <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Labs</Text>
                    <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', borderRadius: '10px' }} ml={4} mr={'6px'} onClick={() => setShowSelfEntered(!showSelfEntered)}>
                      <Text style={{ userSelect: 'none' }} fontSize={'11px'} textAlign={'center'}>{!showSelfEntered?'Show':'Hide'} patient's self entered labs</Text>
                    </Box>
                  </Flex>
                  <Box flex={1} height={isMinimized ? '150px' : ''} minHeight={isMinimized ? '150px' : ''} id='rcWrap'>
                    {!!loading ? (
                      <Skeleton width={'calc(100% - 24px)'} height='calc(100% - 12px)' style={{ borderRadius: '0 0 30px 30px', margin: '0 12px 12px 12px' }} />
                    ) : (
                      <ResponsiveContainer width="100%" height={"100%"}>
                        <ComposedChart
                          data={(combinedData || []).filter(x => showSelfEntered ? true : !x.isSelfEntered)}
                          margin={{
                            top: 10,
                            right: 0,
                            left: 0,
                            bottom: 0,
                          }}
                        >
                          <defs>
                            <linearGradient id="colorUvLabsTSH" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="rgb(30, 70, 212) " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="rgb(30, 70, 212) " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsft4" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#344F7A " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#344F7A " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsft3" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#BE05C1 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#BE05C1 " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabstpo" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F7BE2B " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#F7BE2B " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsrt3" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#9B4A01 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#9B4A01 " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsvitD" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#000000 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#000000 " stopOpacity={0.05} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="x"
                            type="category"
                            ticks={allTicks}
                            interval={0}
                            tick={CustomizedTick}

                          />
                          <YAxis
                            domain={[0, Math.ceil(max * 1.1)]}
                            allowDataOverflow={true}
                            tick={CustomizedTick2}
                          />
                          <Area type="monotone" dataKey="y" stroke={
                            selected === 'TSH' ? "#1E46D4" : selected === 'ft4' ? '#344F7A' : selected === 'ft3' ? '#BE05C1' : selected === 'tpo' ? '#F7BE2B' :
                              selected === 'rt3' ? '#9B4A01' : selected === 'vitD' ? '#000000' : '#1E46D4'
                          } fill={`url(#colorUvLabs${selected})`}
                            connectNulls={true} />

                          <Scatter
                            data={displayData}
                            dataKey="y"
                            shape={CustomScatterShape}
                            name="combined points"
                            stroke={
                              selected === 'TSH' ? "#1E46D4" : selected === 'ft4' ? '#344F7A' : selected === 'ft3' ? '#BE05C1' : selected === 'tpo' ? '#F7BE2B' :
                                selected === 'rt3' ? '#9B4A01' : selected === 'vitD' ? '#000000' : '#1E46D4'
                            }
                            strokeWidth={2}
                            fill={'white'}
                          />

                          <Tooltip content={<CustomizedTooltip selected={selected} />} />

                        </ComposedChart>
                      </ResponsiveContainer>
                    )}

                  </Box>
                </Flex>

                <Box height={isMinimized ? '1px' : '236px'} width={isMinimized ? '100%' : '1px'} backgroundColor={'rgba(52, 79, 122, 0.41)'}>

                </Box>

                <Flex flexDirection={isMinimized ? 'row' : 'column'} flexWrap={'wrap'} padding='24px 24px' justifyContent={'center'}>
                  <RadioButton disabled={!data?.providerGetLabChartValues.tsh || data?.providerGetLabChartValues.tsh.length < 1 || data?.providerGetLabChartValues.tsh.filter(x => x.value !== null).length < 1} label="TSH" value={selected === 'TSH'} onChange={() => { setSelected('TSH') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.providerGetLabChartValues.ft4 || data?.providerGetLabChartValues.ft4.length < 1 || data?.providerGetLabChartValues.ft4.filter(x => x.value !== null).length < 1} label="Free T4" value={selected === 'ft4'} onChange={() => { setSelected('ft4') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.providerGetLabChartValues.ft3 || data?.providerGetLabChartValues.ft3.length < 1 || data?.providerGetLabChartValues.ft3.filter(x => x.value !== null).length < 1} label="Free T3" value={selected === 'ft3'} onChange={() => { setSelected('ft3') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.providerGetLabChartValues.tpo || data?.providerGetLabChartValues.tpo.length < 1 || data?.providerGetLabChartValues.tpo.filter(x => x.value !== null).length < 1} label="TPO antibodies" value={selected === 'tpo'} onChange={() => { setSelected('tpo') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.providerGetLabChartValues.rT3 || data?.providerGetLabChartValues.rT3.length < 1 || data?.providerGetLabChartValues.rT3.filter(x => x.value !== null).length < 1} label="Reverse T3" value={selected === 'rt3'} onChange={() => { setSelected('rt3') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.providerGetLabChartValues.vitD || data?.providerGetLabChartValues.vitD.length < 1 || data?.providerGetLabChartValues.vitD.filter(x => x.value !== null).length < 1} label="Vitamin D" value={selected === 'vitD'} onChange={() => { setSelected('vitD') }} />
                </Flex>
              </Flex>
              <CustomLegend stroke={
                selected === 'TSH' ? "#1E46D4" : selected === 'ft4' ? '#344F7A' : selected === 'ft3' ? '#BE05C1' : selected === 'tpo' ? '#F7BE2B' :
                  selected === 'rt3' ? '#9B4A01' : selected === 'vitD' ? '#000000' : '#1E46D4'} />
            </Flex>
            <ContainerMobile flexDirection={'column'} display={['initial', 'none', 'none']} height={['400px', 0, 0]}>
              <Text mb="20px">Lab Results</Text>
              <Flex flexWrap={'wrap'} style={{ justifyContent: 'center', marginBottom: '24px' }}>
                <PeriodSelector
                  isDisabled={!data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.tsh || data.providerGetLabChartValues.tsh.length < 1 || data.providerGetLabChartValues.tsh.filter((x) => x.value !== null).length < 1}
                  period="TSH"
                  isSelected={selected === 'TSH'}
                  onClick={() => {
                    setSelected('TSH');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.ft4 || data.providerGetLabChartValues.ft4.length < 1 || data.providerGetLabChartValues.ft4.filter((x) => x.value !== null).length < 1}
                  period="fT4"
                  isSelected={selected === 'ft4'}
                  onClick={() => {
                    setSelected('ft4');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.ft3 || data.providerGetLabChartValues.ft3.length < 1 || data.providerGetLabChartValues.ft3.filter((x) => x.value !== null).length < 1}
                  period="fT3"
                  isSelected={selected === 'ft3'}
                  onClick={() => {
                    setSelected('ft3');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.tpo || data.providerGetLabChartValues.tpo.length < 1 || data.providerGetLabChartValues.tpo.filter((x) => x.value !== null).length < 1}
                  period="TPO"
                  isSelected={selected === 'tpo'}
                  onClick={() => {
                    setSelected('tpo');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.rT3 || data.providerGetLabChartValues.rT3.length < 1 || data.providerGetLabChartValues.rT3.filter((x) => x.value !== null).length < 1}
                  period="rT3"
                  isSelected={selected === 'rt3'}
                  onClick={() => {
                    setSelected('rt3');
                  }}
                />
                <PeriodSelector
                  isDisabled={
                    !data || !data.providerGetLabChartValues || !data.providerGetLabChartValues.vitD || data.providerGetLabChartValues.vitD.length < 1 || data.providerGetLabChartValues.vitD.filter((x) => x.value !== null).length < 1
                  }
                  period="vit. D"
                  isSelected={selected === 'vitD'}
                  onClick={() => {
                    setSelected('vitD');
                  }}
                />
              </Flex>
              <Flex flex={1} marginX='-30px'>

                <ResponsiveContainer width="100%" height="97%">
                  <ComposedChart
                    data={values || []}
                    margin={{
                      top: 10,
                      right: 0,
                      left: 0,
                      bottom: 0,
                    }}
                  >
                    <defs>
                      <linearGradient id="colorUvLabsTSH" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="rgb(30, 70, 212) " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="rgb(30, 70, 212) " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsft4" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#344F7A " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#344F7A " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsft3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#BE05C1 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#BE05C1 " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabstpo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F7BE2B " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#F7BE2B " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsrt3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9B4A01 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#9B4A01 " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsvitD" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#000000 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#000000 " stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Tooltip content={<CustomizedTooltip />} />
                    <XAxis axisLine={false} dataKey="x" tick={CustomizedTick} padding={{
                      left: 16,
                      right: 16
                    }} />
                    <YAxis hide />
                    <Area
                      type="monotone"
                      dataKey="y"
                      strokeWidth={'3px'}

                      stroke={
                        selected === 'TSH'
                          ? '#1E46D4'
                          : selected === 'ft4'
                            ? '#344F7A'
                            : selected === 'ft3'
                              ? '#BE05C1'
                              : selected === 'tpo'
                                ? '#F7BE2B'
                                : selected === 'rt3'
                                  ? '#9B4A01'
                                  : selected === 'vitD'
                                    ? '#000000'
                                    : '#1E46D4'
                      }
                      fill={`transparent`}
                    />
                    <Scatter
                      dataKey={'y'}
                      stroke={
                        selected === 'TSH'
                          ? '#1E46D4'
                          : selected === 'ft4'
                            ? '#344F7A'
                            : selected === 'ft3'
                              ? '#BE05C1'
                              : selected === 'tpo'
                                ? '#F7BE2B'
                                : selected === 'rt3'
                                  ? '#9B4A01'
                                  : selected === 'vitD'
                                    ? '#000000'
                                    : '#1E46D4'
                      }
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Flex>
            </ContainerMobile>

          </>
        )}
    </Flex>
  )
}

export default Labs